import React, { useEffect, Dispatch, SetStateAction, useCallback } from 'react'
import { PLPFilterTypes } from '@corratech/pylot-filters-and-sort'
import { useTranslation } from 'next-i18next/pages'
import { Sort, SortDropdown } from '@corratech/pylot-filters-and-sort'
import { Maybe, SortFields } from '@pylot-data/fwrdschema'
import { Grid4, Grid7 } from '@components/icons'
import cn from 'classnames'
import s from './Toolbar.module.scss'
import { Button } from '@corratech/pylot-ui'

type ToolbarProps = {
    horizontal_filter?: boolean
    sortEnabled?: boolean
    sortFields?: Maybe<SortFields>
    filtersToggleEnabled?: boolean
    plpManager: PLPFilterTypes.PLPManager
    activeLabel: string
    setActiveLabel: Dispatch<SetStateAction<string>>
    isSortVisible: boolean
    setIsSortVisible: Dispatch<SetStateAction<boolean>>
    /** NUEVO: control de columnas de la grilla */
    gridColumns: 4 | 7
    setGridColumns: Dispatch<SetStateAction<4 | 7>>
}

export const Toolbar: React.FC<ToolbarProps> = (props): JSX.Element => {
    const { t } = useTranslation('plp')

    const {
        isSortVisible,
        setIsSortVisible,
        horizontal_filter = true,
        sortEnabled = true,
        sortFields = {},
        filtersToggleEnabled = true,
        plpManager: {
            plpState: { filtersVisible, sort, appliedFilterData },
            setFiltersVisible,
            applySort
        },
        activeLabel,
        setActiveLabel,
        /** NUEVO */
        gridColumns,
        setGridColumns
    } = props

    useEffect(() => {
        document.body.classList.remove('modal-open-mob')
        if (filtersVisible || isSortVisible) {
            document.body.classList.add('modal-open-mob')
        }
        // Al desmontar (cambio de ruta con el panel abierto) la clase no debe
        // quedar en el body de la página destino.
        return () => document.body.classList.remove('modal-open-mob')
    }, [filtersVisible, isSortVisible])

    const filterCount = appliedFilterData.length

    const handleGridSize = useCallback(
        (cols: 4 | 7) => () => {
            setGridColumns(cols)
            setIsSortVisible(false)
            setFiltersVisible(false)
        },
        [setGridColumns, setFiltersVisible, setIsSortVisible]
    )

    return (
        <div className={s['product-list-toolbar']}>
            {horizontal_filter ? (
                <div className={s['toolbar-items']}>
                    <div className={s['toggle-filters']}>
                        <div className={s['toggle-filters-filter']}>
                            {filtersToggleEnabled && (
                                <Button
                                    className={s['btn-label']}
                                    onClick={() => {
                                        setFiltersVisible(!filtersVisible)
                                        setIsSortVisible(false)
                                    }}
                                    variant="secondary"
                                >
                                    <img
                                        className={s['filter-icon']}
                                        src="/icons/mage_filter.svg"
                                        alt={t('Filter By Alt')}
                                    />
                                    {t('toolbar|Filter By')}
                                    <span className="filter-count ml-1">
                                        {filterCount ? `(${filterCount})` : ''}
                                    </span>
                                </Button>
                            )}
                            {sortEnabled && sortFields && (
                                <Button
                                    className={cn(
                                        s['btn-label'],
                                        s['btn-sort']
                                    )}
                                    variant="secondary"
                                    onClick={() => {
                                        setIsSortVisible(!isSortVisible)
                                        setFiltersVisible(false)
                                    }}
                                >
                                    {t('toolbar|Sort By')}
                                    <img
                                        className={s['orderby-icon']}
                                        src="/icons/Icon-Arrow-red.svg"
                                        alt={t('Sort By Alt')}
                                    />
                                </Button>
                            )}
                        </div>

                        <div className={s['grid-size']}>
                            <Button
                                className={cn(s['btn-label'], s['btn-grid'], {
                                    [s.active]: gridColumns === 7
                                })}
                                variant="secondary"
                                aria-pressed={gridColumns === 7}
                                aria-label={t('toolbar|Grid 7 products')}
                                title={t('toolbar|7 per row')}
                                onClick={handleGridSize(7)}
                            >
                                <Grid4 className={s.icon} />
                            </Button>
                            <Button
                                className={cn(s['btn-label'], s['btn-grid'], {
                                    [s.active]: gridColumns === 4
                                })}
                                variant="secondary"
                                aria-pressed={gridColumns === 4}
                                aria-label={t('toolbar|Grid 4 products')}
                                title={t('toolbar|4 per row')}
                                onClick={handleGridSize(4)}
                            >
                                <Grid7 className={s.icon} />
                            </Button>
                        </div>
                    </div>

                    {isSortVisible && sortFields && (
                        <Sort
                            applySort={applySort}
                            sortFields={sortFields}
                            appliedSort={sort}
                            isSortVisible={isSortVisible}
                            setIsSortVisible={setIsSortVisible}
                            activeLabel={activeLabel}
                            setActiveLabel={setActiveLabel}
                        />
                    )}
                </div>
            ) : (
                <div className={cn(s['toolbar-items'], s['vertical'])}>
                    {filtersToggleEnabled && (
                        <div className={s['toggle-filters']}>
                            <button
                                onClick={() =>
                                    setFiltersVisible(!filtersVisible)
                                }
                            >
                                {t('Filter')}
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="15"
                                    height="15"
                                    viewBox="0 0 16 16"
                                >
                                    <g
                                        fill="none"
                                        fillRule="evenodd"
                                        stroke="none"
                                        strokeWidth="1"
                                    >
                                        <g
                                            fill="#000"
                                            fillRule="nonzero"
                                            transform="translate(-38 -197)"
                                        >
                                            <g transform="translate(20 185)">
                                                <g transform="translate(16 10)">
                                                    <g>
                                                        <path d="M2.5 14.167v1.666h5v-1.666h-5zm0-10v1.666h8.333V4.167H2.5zM10.833 17.5v-1.667H17.5v-1.666h-6.667V12.5H9.167v5h1.666zm-5-10v1.667H2.5v1.666h3.333V12.5H7.5v-5H5.833zM17.5 10.833V9.167H9.167v1.666H17.5zm-5-3.333h1.667V5.833H17.5V4.167h-3.333V2.5H12.5v5z" />
                                                    </g>
                                                </g>
                                            </g>
                                        </g>
                                    </g>
                                </svg>
                            </button>
                        </div>
                    )}
                    {sortEnabled && sortFields && (
                        <SortDropdown
                            applySort={applySort}
                            sortFields={sortFields}
                            appliedSort={sort}
                            activeLabel={activeLabel}
                            setActiveLabel={setActiveLabel}
                        />
                    )}
                </div>
            )}
            {props.children}
        </div>
    )
}
